import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Callout } from '@client/components/_ui/feedback/Callout';
import { InlineLink } from '@client/components/Common/Link';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton, Tag } from 'antd';
import {
    Briefcase,
    Building2,
    ChevronRight,
    Clock,
    KeyRound,
    Layers,
    Mail,
    Phone,
    Smartphone,
    Tags,
    User,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { type FleetClientRecord, formatClientAddress, isConsolidatorRecord } from './ClientRecordHeader';
import { useCanManageFleetClients, useClientRecord, useClientRecordActions } from './client-record-data';
import {
    EmptyValue,
    Field,
    FieldGrid,
    InitialsAvatar,
    RecordCard,
    SummaryRow,
    ValueChip,
    ValueOrEmpty,
} from './record-primitives';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database/details/$id/overview')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <OverviewTab />
        </PageController>
    );
}

function OverviewTab() {
    const { id } = Route.useParams();
    const { data: client } = useClientRecord(id);

    if (!client) {
        return <Skeleton active />;
    }

    return isConsolidatorRecord(client) ? <ConsolidatorOverview client={client} /> : <ClientOverview client={client} />;
}

function DetailsCard({ client }: { client: FleetClientRecord }) {
    const orgId = useOrgId();
    const isConsolidator = isConsolidatorRecord(client);

    return (
        <RecordCard
            icon={isConsolidator ? Layers : Building2}
            title={isConsolidator ? 'Consolidator details' : 'Client details'}
        >
            <FieldGrid columns={3}>
                <Field label="Client no">
                    <span className="tabular-nums">{client.clientNo}</span>
                </Field>
                <Field label="Record type">
                    {isConsolidator ? (
                        <Tag variant="outlined" color="purple" className="m-0">
                            Consolidator
                        </Tag>
                    ) : (
                        <Tag variant="outlined" className="m-0">
                            Client
                        </Tag>
                    )}
                </Field>
                <Field label="Status">
                    <ValueOrEmpty value={client.status} />
                </Field>
                {!isConsolidator ? (
                    <Field label="Consolidator">
                        {client.Parent ? (
                            <InlineLink
                                to="/console/$orgId/configuration/fleet/client-database/details/$id"
                                params={{ orgId, id: client.Parent.id }}
                            >
                                {client.Parent.name}
                            </InlineLink>
                        ) : (
                            <EmptyValue>No consolidator</EmptyValue>
                        )}
                    </Field>
                ) : null}
                <Field label="ABN">
                    <span className="tabular-nums">
                        <ValueOrEmpty value={client.abn} />
                    </span>
                </Field>
                <Field label="Address" span={isConsolidator}>
                    <ValueOrEmpty value={formatClientAddress(client)} />
                </Field>
            </FieldGrid>
        </RecordCard>
    );
}

function ContactLine({ icon: Icon, children }: { icon: typeof Mail; children: ReactNode }) {
    return (
        <span className="flex min-w-0 items-center gap-2 text-sm">
            <Icon className="size-3.5 shrink-0 text-subtle-foreground" />
            <span className="truncate">{children}</span>
        </span>
    );
}

function PrimaryContactCard({ client }: { client: FleetClientRecord }) {
    const name = [client.contactTitle, client.contactFirstName, client.contactLastName].filter(Boolean).join(' ');
    const hasAnyContact =
        name || client.contactJobTitle || client.contactEmail || client.contactPhone || client.contactMobile;

    return (
        <RecordCard icon={User} title="Primary contact">
            {hasAnyContact ? (
                <>
                    <div className="flex items-center gap-3">
                        <InitialsAvatar
                            name={[client.contactFirstName, client.contactLastName].filter(Boolean).join(' ') || '?'}
                        />
                        <div className="min-w-0">
                            <div className="font-semibold text-sm leading-tight">
                                {name || <EmptyValue>No name recorded</EmptyValue>}
                            </div>
                            <div className="text-subtle-foreground text-xs">
                                <ValueOrEmpty value={client.contactJobTitle} empty="No job title" />
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1.9fr)_minmax(0,1fr)_minmax(0,1fr)]">
                        <ContactLine icon={Mail}>
                            {client.contactEmail ? (
                                <a href={`mailto:${client.contactEmail}`}>{client.contactEmail}</a>
                            ) : (
                                <EmptyValue>No email</EmptyValue>
                            )}
                        </ContactLine>
                        <ContactLine icon={Phone}>
                            {client.contactPhone ? (
                                <span className="tabular-nums">{client.contactPhone}</span>
                            ) : (
                                <EmptyValue>No phone</EmptyValue>
                            )}
                        </ContactLine>
                        <ContactLine icon={Smartphone}>
                            {client.contactMobile ? (
                                <span className="tabular-nums">{client.contactMobile}</span>
                            ) : (
                                <EmptyValue>No mobile</EmptyValue>
                            )}
                        </ContactLine>
                    </div>
                </>
            ) : (
                <EmptyValue>No primary contact recorded.</EmptyValue>
            )}
        </RecordCard>
    );
}

function AccountTeamCard({ client }: { client: FleetClientRecord }) {
    const { openEdit } = useClientRecordActions();
    const canEdit = useCanManageFleetClients();
    const isConsolidator = isConsolidatorRecord(client);

    return (
        <RecordCard
            icon={Briefcase}
            title="Account team"
            subtitle={`Fleet provider's people on this ${isConsolidator ? 'consolidator' : 'client'}`}
        >
            <FieldGrid columns={2}>
                <Field label="Account manager">
                    <ValueOrEmpty value={client.accountManagerName} />
                </Field>
                <Field label="BDM">
                    <ValueOrEmpty value={client.bdmName} />
                </Field>
                <Field label="Account manager email">
                    {client.accountManagerEmail ? (
                        <a href={`mailto:${client.accountManagerEmail}`}>{client.accountManagerEmail}</a>
                    ) : (
                        <EmptyValue />
                    )}
                </Field>
                <Field label="Account manager phone">
                    {client.accountManagerPhone ? (
                        <span className="tabular-nums">{client.accountManagerPhone}</span>
                    ) : (
                        <EmptyValue />
                    )}
                </Field>
            </FieldGrid>
            {isConsolidator ? (
                <p className="m-0 text-subtle-foreground text-xs">
                    Escalation notices are resolved per client, so the account manager email lives on each client
                    record.
                </p>
            ) : !client.accountManagerEmail ? (
                <Callout variant="warning" title="Escalation notices have nowhere to go">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <span>
                            No account manager email is recorded, so escalation notices cannot be sent to the account
                            manager until one is added.
                        </span>
                        {canEdit ? (
                            <Button type="link" size="small" className="px-0!" onClick={openEdit}>
                                Add email
                            </Button>
                        ) : null}
                    </div>
                </Callout>
            ) : (
                <p className="m-0 text-subtle-foreground text-xs">
                    Escalation notices for this client's claims go to the account manager email.
                </p>
            )}
        </RecordCard>
    );
}

function RecordCardDates({ client }: { client: FleetClientRecord }) {
    const { formatDate, formatDateTime } = useFormatter();
    return (
        <RecordCard icon={Clock} title="Record">
            <div className="flex flex-col">
                <SummaryRow label="Created">{formatDate(client.createdAt)}</SummaryRow>
                <SummaryRow label="Last updated">{formatDateTime(client.updatedAt)}</SummaryRow>
            </div>
        </RecordCard>
    );
}

function FleetPortalCard({ client }: { client: FleetClientRecord }) {
    const orgId = useOrgId();
    const { formatCurrency } = useFormatter();
    const usersQuery = trpc.fleetClientDb.listPortalUsers.useQuery(
        client.id ? { fleetClientId: client.id } : skipToken,
    );
    const activeUsers = usersQuery.data?.rows.filter((row) => row.status === 'ACTIVE').length;

    return (
        <RecordCard
            icon={KeyRound}
            title="Fleet portal"
            footer={
                <InlineLink
                    to="/console/$orgId/configuration/fleet/client-database/details/$id/portal-access"
                    params={{ orgId, id: client.id }}
                    className="inline-flex items-center gap-1 text-xs"
                >
                    Manage portal access
                    <ChevronRight className="size-3" />
                </InlineLink>
            }
        >
            <div className="flex flex-col">
                <SummaryRow label="Delegated authority (ex GST)">
                    {client.authorityLimit === null ? (
                        <EmptyValue>Organisation default</EmptyValue>
                    ) : (
                        formatCurrency(Number(client.authorityLimit))
                    )}
                </SummaryRow>
                <SummaryRow label="Portal users">
                    {usersQuery.data ? (
                        `${activeUsers} active`
                    ) : usersQuery.error ? (
                        <EmptyValue>Unavailable</EmptyValue>
                    ) : (
                        <EmptyValue>Loading</EmptyValue>
                    )}
                </SummaryRow>
            </div>
            <p className="m-0 text-subtle-foreground text-xs">
                Repairer quotes at or under the authority are approved automatically; anything above goes to the
                client's fleet manager.
            </p>
        </RecordCard>
    );
}

function CostCentresCard({ client }: { client: FleetClientRecord }) {
    return (
        <RecordCard icon={Tags} title="Cost centres and depots">
            <Field label={`Cost centres · ${client.FleetCostCentre.length}`}>
                {client.FleetCostCentre.length ? (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                        {client.FleetCostCentre.map((costCentre) => (
                            <ValueChip key={String(costCentre.id)}>{costCentre.name}</ValueChip>
                        ))}
                    </div>
                ) : (
                    <EmptyValue>None recorded</EmptyValue>
                )}
            </Field>
            <Field label="Depots">
                {client.FleetDepot.length ? (
                    <div className="flex w-full flex-col gap-1.5">
                        {client.FleetDepot.map((depot) => (
                            <div key={String(depot.id)} className="text-sm">
                                <span className="font-medium">{depot.name}</span>
                                <span className="text-subtle-foreground">
                                    {[depot.address, depot.suburb, depot.state, depot.managerName].filter(Boolean)
                                        .length
                                        ? ` · ${[depot.address, depot.suburb, depot.state, depot.managerName].filter(Boolean).join(', ')}`
                                        : ''}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <span className="flex flex-col gap-0.5">
                        <EmptyValue>None recorded</EmptyValue>
                        <span className="text-subtle-foreground text-xs">
                            Depot fields are not part of the current import feed.
                        </span>
                    </span>
                )}
            </Field>
        </RecordCard>
    );
}

function BookCard({ client }: { client: FleetClientRecord }) {
    const orgId = useOrgId();
    const stats = [
        { label: 'Clients', value: client.childrenCount, note: 'linked to this consolidator' },
        { label: 'Vehicles', value: client.childVehiclesCount, note: 'active leases across clients' },
        { label: 'Drivers', value: client.childDriversCount, note: 'with a vehicle' },
    ];

    return (
        <RecordCard
            icon={Building2}
            title="Book"
            padded={false}
            footer={
                <InlineLink
                    to="/console/$orgId/configuration/fleet/client-database/details/$id/clients"
                    params={{ orgId, id: client.id }}
                    className="inline-flex items-center gap-1 text-xs"
                >
                    View clients
                    <ChevronRight className="size-3" />
                </InlineLink>
            }
        >
            <dl className="m-0 grid grid-cols-3">
                {stats.map((stat) => (
                    <div
                        key={stat.label}
                        className="flex flex-col border-separator-secondary border-r px-4 py-3.5 last:border-r-0"
                    >
                        <dt className="font-semibold text-2xs text-subtle-foreground uppercase tracking-wide">
                            {stat.label}
                        </dt>
                        <dd className="m-0 font-semibold text-2xl tabular-nums leading-tight">{stat.value}</dd>
                        <dd className="m-0 text-subtle-foreground text-xs">{stat.note}</dd>
                    </div>
                ))}
            </dl>
        </RecordCard>
    );
}

function ClientOverview({ client }: { client: FleetClientRecord }) {
    return (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col gap-4">
                <DetailsCard client={client} />
                <PrimaryContactCard client={client} />
                <AccountTeamCard client={client} />
            </div>
            <div className="flex min-w-0 flex-col gap-4">
                <FleetPortalCard client={client} />
                <CostCentresCard client={client} />
                <RecordCardDates client={client} />
            </div>
        </div>
    );
}

function ConsolidatorOverview({ client }: { client: FleetClientRecord }) {
    return (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col gap-4">
                <DetailsCard client={client} />
                <PrimaryContactCard client={client} />
                <AccountTeamCard client={client} />
            </div>
            <div className="flex min-w-0 flex-col gap-4">
                <BookCard client={client} />
                <RecordCardDates client={client} />
            </div>
        </div>
    );
}
